import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import StatCard from '../components/StatCard';
import { SOBlock, todayISO } from '../components/BlockedOrdersShared';
import OrderTraceModal from '../components/OrderTraceModal';

// ── Page ──────────────────────────────────────────────────────────────────────
// Mirrors BlockedOrders.jsx exactly -- same notes table, same actions
// (assign, urgent, next action, follow-up), same SOBlock component -- just
// scoped to a single stage (HC - Back Order) via its own RPC. Sharing the
// notes table means if a back order later escalates to Problem Encountered,
// its full history is already sitting there on that page too.
const BackOrderManagement = () => {
  const navigate = useNavigate();
  const [orders, setOrders]           = useState([]);
  const [notesByOrder, setNotesByOrder] = useState({});
  const [assignedByOrder, setAssignedByOrder] = useState({});
  const [staffList, setStaffList]     = useState([]);
  const [nextActions, setNextActions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [staffFilter, setStaffFilter] = useState('');
  const [showAll, setShowAll]         = useState(false);
  const [traceInput, setTraceInput]   = useState('');
  const [traceRef, setTraceRef]       = useState(null);

  const loadStaff = useCallback(async () => {
    const { data } = await supabase.from('staff').select('name, initials').order('name');
    setStaffList(data || []);
  }, []);

  const loadNextActions = useCallback(async () => {
    const { data } = await supabase
      .from('blocked_order_next_actions')
      .select('id, label, sort_order')
      .eq('active', true)
      .order('sort_order');
    setNextActions(data || []);
  }, []);

  const loadNotes = useCallback(async () => {
    const { data } = await supabase
      .from('blocked_order_notes')
      .select('id, order_id, note, initials, created_at, follow_up_date')
      .order('created_at', { ascending: false });
    const map = {};
    for (const n of data || []) {
      (map[n.order_id] ??= []).push(n);
    }
    setNotesByOrder(map);
  }, []);

  const loadAssignments = useCallback(async () => {
    const { data } = await supabase
      .from('blocked_order_assignments')
      .select('order_id, initials, urgent');
    const map = {};
    for (const a of data || []) {
      map[a.order_id] = { initials: a.initials, urgent: a.urgent };
    }
    setAssignedByOrder(map);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);

    const { data, error } = await supabase.rpc('get_backorder_worklist');

    if (error) {
      console.error('get_backorder_worklist error:', error);
      setOrders([]);
      setLoading(false);
      return;
    }

    const rows = data || [];

    const orderMap = new Map();
    for (const row of rows) {
      if (!orderMap.has(row.order_id)) {
        orderMap.set(row.order_id, {
          order_id:     row.order_id,
          order_ref:    row.order_ref,
          created_date: row.created_date,
          stage:        row.stage,
          status:       row.status,
          first_name:   row.first_name || '',
          last_name:    row.last_name  || '',
          email:        row.email || '',
          phone:        row.phone || '',
          internal_comments: row.internal_comments || '',
          items:        [],
        });
      }
      if (row.item_code) {
        orderMap.get(row.order_id).items.push({
          item_code:   row.item_code,
          item_name:   row.item_name || row.item_code,
          qty_ordered: row.qty_ordered,
          qty_moved:   row.qty_moved ?? 0,
          po_number:   row.po_number,
        });
      }
    }

    setOrders([...orderMap.values()]);
    setLoading(false);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => {
    load(); loadNotes(); loadStaff(); loadAssignments(); loadNextActions();
  }, [load, loadNotes, loadStaff, loadAssignments, loadNextActions]);

  const handleNoteAdded = (orderId, newNote) => {
    setNotesByOrder((prev) => ({
      ...prev,
      [orderId]: [newNote, ...(prev[orderId] || [])],
    }));
  };

  const handleAssign = (orderId, initials) => {
    setAssignedByOrder((prev) => ({ ...prev, [orderId]: { ...(prev[orderId] || {}), initials } }));
  };

  const handleUrgentChange = (orderId, urgent) => {
    setAssignedByOrder((prev) => ({ ...prev, [orderId]: { ...(prev[orderId] || {}), urgent } }));
  };

  // The order's current follow-up date is whatever its most recent note set —
  // notesByOrder is sorted newest-first, so [0] is the live schedule.
  const followUpFor = (orderId) => notesByOrder[orderId]?.[0]?.follow_up_date || null;
  const today = todayISO();

  const filtered = orders
    .filter(o => {
      if (!staffFilter) return true;
      const initials = assignedByOrder[o.order_id]?.initials || '';
      if (staffFilter === '__unassigned__') return !initials;
      return initials === staffFilter;
    })
    .filter(o => {
      if (showAll) return true;
      const dueDate = followUpFor(o.order_id);
      return !dueDate || dueDate <= today;
    });

  const needingAttentionCount = orders.filter(o => {
    const dueDate = followUpFor(o.order_id);
    return !dueDate || dueDate <= today;
  }).length;
  const scheduledCount = orders.filter(o => {
    const dueDate = followUpFor(o.order_id);
    return dueDate && dueDate > today;
  }).length;

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/utilities')}>← Utilities</button>
        <span className="page-nav__title">Back Order Management</span>
        <button className="back-btn" onClick={() => navigate('/blocked-orders')}>🚧 Problem Encountered</button>
        <button className="back-btn" onClick={() => navigate('/backorder-follow-up-schedule')}>📅 Follow-Up Schedule</button>
        <button className="back-btn" onClick={() => navigate('/backorder-history')}>🗂 History</button>
        <span className="app-header__refresh">Last refresh: {lastRefresh.toLocaleTimeString('en-NZ')}</span>
        <button className="app-header__btn" onClick={load}>↻ Refresh</button>
      </div>

      <div style={{ padding: '18px 32px 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--white)' }}>🔍 Trace Order</span>
        <input
          type="text"
          placeholder="e.g. OJUN84407-33"
          value={traceInput}
          onChange={e => setTraceInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && traceInput.trim()) setTraceRef(traceInput.trim()); }}
          style={{
            background: 'var(--bg3)', color: 'var(--white)',
            border: '1px solid var(--border2)', borderRadius: 4,
            padding: '8px 14px', fontFamily: 'var(--font-body)',
            fontSize: 14, width: 230,
          }}
        />
        <button
          className="action-btn action-btn--ghost"
          style={{ fontSize: 14, padding: '8px 18px' }}
          disabled={!traceInput.trim()}
          onClick={() => setTraceRef(traceInput.trim())}
        >
          Trace
        </button>
      </div>
      {traceRef && (
        <OrderTraceModal orderRef={traceRef} onClose={() => setTraceRef(null)} />
      )}

      <div className="stats-row">
        <StatCard
          label="HC - Back Order"
          value={orders.length}
          accent="blue"
          breakdown={[
            { label: 'Needing Attention', value: needingAttentionCount },
            { label: 'Scheduled',         value: scheduledCount },
          ]}
        />
      </div>

      <section className="section">
        <SectionHeader
          title="Back Orders"
          count={filtered.length}
          onRefresh={load}
          loading={loading}
          actions={
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className={`action-btn ${showAll ? 'action-btn--active' : 'action-btn--ghost'}`}
                onClick={() => setShowAll(v => !v)}
                style={{ fontSize: 12 }}
              >
                {showAll ? 'Show All ✓' : 'Show All'}
              </button>
              <select
                value={staffFilter}
                onChange={e => setStaffFilter(e.target.value)}
                style={{
                  background: 'var(--bg3)', color: 'var(--white)',
                  border: '1px solid var(--border2)', borderRadius: 4,
                  padding: '4px 10px', fontFamily: 'var(--font-body)',
                  fontSize: 12, cursor: 'pointer',
                }}
              >
                <option value="">All Staff</option>
                <option value="__unassigned__">Unassigned</option>
                {staffList.map((s) => (
                  <option key={s.initials} value={s.initials}>{s.name} ({s.initials})</option>
                ))}
              </select>
            </div>
          }
        />
        <p className="section__desc">
          {showAll
            ? 'All sales orders currently at HC - Back Order, including those scheduled for a later date.'
            : 'Orders to deal with today: no schedule set, or due today or overdue. Toggle "Show All" to include orders scheduled for a later date.'}
        </p>

        {loading && <div className="loading-row">Loading…</div>}
        {!loading && !filtered.length && (
          <div className="empty-row">✓ No back orders</div>
        )}
        {!loading && filtered.map(order => (
          <SOBlock
            key={order.order_id}
            order={order}
            notes={notesByOrder[order.order_id] || []}
            staffList={staffList}
            nextActions={nextActions}
            assignedInitials={assignedByOrder[order.order_id]?.initials}
            urgent={assignedByOrder[order.order_id]?.urgent}
            onNoteAdded={handleNoteAdded}
            onAssign={handleAssign}
            onUrgentChange={handleUrgentChange}
          />
        ))}
      </section>
    </main>
  );
};

export default BackOrderManagement;
